/**
 * Procurement Group Table Components
 * Renders a grouped procurement table with desktop and mobile layouts.
 */

import { useState, type FocusEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AlertTriangle, ChevronDown, ChevronUp, Info, RotateCcw, Star, X } from 'lucide-react';
import { QuantitySaveIndicator, type QuantitySaveState } from '@/components/admin/QuantitySaveIndicator';
import { formatPackCount, formatQuantity, formatRecipeQuantity, getBreakdownScaleRatios, getBreakdownSizeCounts, getBreakdownSizeOptOutCounts, getItemOrderIncrement, getProcurementRowState, getVendorName } from './helpers';
import { UNASSIGNED_VENDOR_LABEL } from '@/types/api';
import { DEFAULT_RSVP_SIZE_SCALE_RATIOS, RSVP_SIZES, formatScaledQuantity, getAutoConversionMultiplier } from '@/types/units';
import type { IngredientVendor, ProcurementBreakdownEntry, ProcurementItem } from '@/types/api';
import type {
  ProcurementDisplayItem,
  ProcurementGroup,
  ProcurementItemDraft,
  ProcurementViewMode,
  RegenDiffEntry,
  RegenDiffMap,
} from './types';

const UNASSIGNED_VALUE = '__unassigned__';
const LOADING_VALUE = '__loading__';
const IDLE_SAVE_STATE: QuantitySaveState = { status: 'idle' };

/** Fire only when focus leaves the row entirely (not when moving between its own inputs). */
function handleRowFocusOut(event: FocusEvent<HTMLElement>, onRowBlur: ((itemId: number) => void) | undefined, itemId: number) {
  if (!onRowBlur) return;
  if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
  onRowBlur(itemId);
}

/** Save status shown under the Purchase Qty input; reserves no space while idle. */
function RowSaveStatus({ state, className = '' }: { state: QuantitySaveState; className?: string }) {
  if (state.status === 'idle') return null;
  return (
    <div className={`relative h-4 ${className}`}>
      <QuantitySaveIndicator state={state} />
    </div>
  );
}

type VendorSelectProps = {
  item: ProcurementDisplayItem['item'];
  draft: ProcurementItemDraft;
  vendorSelectClass: string;
  saving: boolean;
  ingredientVendors: IngredientVendor[] | undefined;
  onVendorChange: (itemId: number, vendorId: number | null, vendorName: string | null) => void;
  onFetchIngredientVendors: (ingredientId: number) => Promise<void>;
};

function VendorSelect({
  item,
  draft,
  vendorSelectClass,
  saving,
  ingredientVendors,
  onVendorChange,
  onFetchIngredientVendors,
}: VendorSelectProps) {
  const [loading, setLoading] = useState(false);

  const currentVendorId = draft.vendorId !== undefined ? draft.vendorId : item.vendorId;
  const selectValue = currentVendorId != null ? String(currentVendorId) : UNASSIGNED_VALUE;

  const displayName = draft.vendorId !== undefined
    ? (draft.vendorNameSnapshot ?? UNASSIGNED_VENDOR_LABEL)
    : getVendorName(item);

  const handleOpenChange = (open: boolean) => {
    if (open && !ingredientVendors) {
      setLoading(true);
      onFetchIngredientVendors(item.ingredientId)
        .catch((err) => {
          console.error(`Failed to fetch vendors for ingredient ${item.ingredientId}:`, err);
        })
        .finally(() => setLoading(false));
    }
  };

  const handleValueChange = (value: string) => {
    if (value === UNASSIGNED_VALUE) {
      onVendorChange(item.id, null, null);
      return;
    }
    const numericId = Number(value);
    const vendor = ingredientVendors?.find((v) => v.vendorId === numericId);
    onVendorChange(item.id, numericId, vendor?.vendor?.name ?? null);
  };

  return (
    <Select
      value={selectValue}
      onValueChange={handleValueChange}
      onOpenChange={handleOpenChange}
      disabled={saving}
    >
      <SelectTrigger
        size="sm"
        className={`h-auto max-w-[200px] border-transparent px-1 py-0.5 text-xs shadow-none ${vendorSelectClass}`}
        aria-label={`${item.displayName} vendor`}
      >
        <SelectValue placeholder={UNASSIGNED_VENDOR_LABEL}>
          {displayName}
        </SelectValue>
      </SelectTrigger>
      <SelectContent align="start">
        <SelectItem value={UNASSIGNED_VALUE}>
          {UNASSIGNED_VENDOR_LABEL}
        </SelectItem>
        {loading ? (
          <SelectItem value={LOADING_VALUE} disabled>
            Loading...
          </SelectItem>
        ) : ingredientVendors && ingredientVendors.length > 0 ? (
          <>
            <SelectSeparator />
            {ingredientVendors.map((iv) => (
              <SelectItem key={iv.vendorId} value={String(iv.vendorId)}>
                <span className="flex items-center gap-1.5">
                  {iv.isPreferred && <Star className="h-3 w-3 fill-amber-400 text-amber-400" />}
                  {iv.vendor?.name ?? `Vendor ${iv.vendorId}`}
                </span>
              </SelectItem>
            ))}
          </>
        ) : null}
      </SelectContent>
    </Select>
  );
}

function hasUnitMismatch(breakdown: ProcurementBreakdownEntry[]): boolean {
  if (breakdown.length <= 1) return false;
  const units = new Set(breakdown.map((e) => e.unit));
  return units.size > 1;
}

function formatBreakdownFormula(entry: ProcurementBreakdownEntry): string {
  const quantityPerLarge = Number(entry.quantityPerLarge);
  if (!Number.isFinite(quantityPerLarge)) {
    return `${formatQuantity(entry.quantityPerLarge)}/large = ${formatQuantity(entry.totalQuantity)}`;
  }

  const counts = getBreakdownSizeCounts(entry);
  const ratios = getBreakdownScaleRatios(entry);
  const parts = RSVP_SIZES
    .filter((size) => counts[size] > 0)
    .map((size) => {
      const qtyForSize = formatScaledQuantity(quantityPerLarge * ratios[size]);
      const scaleLabel = Math.abs(ratios[size] - DEFAULT_RSVP_SIZE_SCALE_RATIOS[size]) > 0.0001
        ? ` @${formatScaledQuantity(ratios[size])}`
        : '';
      return `${qtyForSize}/${size}${scaleLabel} × ${counts[size]}${size}`;
    });

  return `${parts.length > 0 ? parts.join(' + ') : '0'} = ${formatQuantity(entry.totalQuantity)}`;
}

function BreakdownPanel({ breakdown, parentUnit }: { breakdown: ProcurementBreakdownEntry[]; parentUnit: string }) {
  const mismatch = hasUnitMismatch(breakdown);
  const convertible = breakdown.every((entry) => getAutoConversionMultiplier(entry.unit, parentUnit) !== null);

  if (breakdown.length === 0) {
    return (
      <div className="px-4 py-3 text-xs text-muted-foreground italic">
        No breakdown data available.
      </div>
    );
  }

  return (
    <div className="space-y-2 rounded-md bg-muted/40 px-4 py-3">
      {mismatch && (
        <div className="flex items-center gap-1.5 rounded bg-amber-50 px-2 py-1 text-xs font-medium text-amber-800">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          {convertible
            ? `Recipes use different units for this ingredient; each line is converted into ${parentUnit} for the total.`
            : 'Unit mismatch — these units cannot be added together. Regenerate the snapshot to list this ingredient once per unit.'}
        </div>
      )}
      <div className="space-y-1.5">
        {breakdown.map((entry, i) => {
          const entryMismatch = mismatch && getAutoConversionMultiplier(entry.unit, parentUnit) === null;
          return (
            <div key={`${entry.menuId}-${entry.recipeId ?? i}`} className="text-xs">
              <div className="flex items-center gap-1.5 font-medium text-foreground">
                <span className="text-muted-foreground">
                  {new Date(entry.menuDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric' })}
                </span>
                <span>·</span>
                <span>{entry.recipeName ?? 'Unknown recipe'}</span>
                {entry.isOptional && (
                  <Badge variant="outline" className="ml-1 px-1 py-0 text-[10px]">Optional</Badge>
                )}
              </div>
              <div className="mt-0.5 flex items-center gap-1.5 pl-2 text-muted-foreground">
                <span className="font-mono">{formatBreakdownFormula(entry)} {entry.unit}</span>
                <OptOutSummary entry={entry} />
                {entryMismatch && (
                  <span className="inline-flex items-center gap-0.5 font-medium text-amber-700">
                    <AlertTriangle className="h-3 w-3" />
                    unit
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Saved on-hand in a unit the list no longer uses; the box is empty so the caterer re-enters it in the new unit. */
function StaleInventoryNote({ entry }: { entry: ProcurementDisplayItem }) {
  if (!entry.inventoryStale) return null;
  return (
    <span
      className="text-xs text-amber-700 whitespace-nowrap"
      title={`Saved before the unit changed; enter the amount in ${entry.inventoryUnit ?? entry.item.unit} to replace it.`}
    >
      was {entry.inventoryStale.quantity} {entry.inventoryStale.unit}
    </span>
  );
}

/** Replaces the On Hand input on a line whose unit cannot net the ingredient's shared on-hand. */
function InventoryTrackedElsewhere({ item, trackedIn }: { item: ProcurementItem; trackedIn: string }) {
  return (
    <span
      className="text-xs text-muted-foreground"
      title={`On hand for ${item.displayName} is tracked in ${trackedIn}; this ${item.unit} line is bought in full.`}
    >
      tracked in {trackedIn}
    </span>
  );
}

function OptOutSummary({ entry }: { entry: ProcurementBreakdownEntry }) {
  if (!entry.isOptional) return null;
  const optOutCounts = getBreakdownSizeOptOutCounts(entry);
  const parts = RSVP_SIZES
    .filter((size) => optOutCounts[size] > 0)
    .map((size) => `-${optOutCounts[size]}${size}`);
  if (parts.length === 0) return null;
  return <span className="text-amber-700">({parts.join(', ')} opted out)</span>;
}

export function ProcurementGroupTable({
  group,
  viewMode,
  saving,
  allBreakdownsExpanded,
  onDraftChange,
  onInventoryChange,
  onToggleExcluded,
  onVendorChange,
  ingredientVendorsCache,
  onFetchIngredientVendors,
  regenDiff = {},
  rowSaveStatus = {},
  onRowBlur,
}: {
  group: ProcurementGroup;
  viewMode: ProcurementViewMode;
  saving: boolean;
  /** null = individual control, true/false = force all open/closed */
  allBreakdownsExpanded: boolean | null;
  onDraftChange: (itemId: number, patch: Partial<ProcurementItemDraft>) => void;
  onInventoryChange: (itemId: number, inventoryValue: string) => void;
  onToggleExcluded: (itemId: number) => void;
  onVendorChange: (itemId: number, vendorId: number | null, vendorName: string | null) => void;
  ingredientVendorsCache: Map<number, IngredientVendor[]>;
  onFetchIngredientVendors: (ingredientId: number) => Promise<void>;
  regenDiff?: RegenDiffMap;
  /** Per-row auto-save status, keyed by item id */
  rowSaveStatus?: Record<number, QuantitySaveState>;
  /** Called when focus leaves a row entirely (used to flush that row's auto-save) */
  onRowBlur?: (itemId: number) => void;
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle>{group.title}</CardTitle>
            <CardDescription>{group.items.length} item{group.items.length === 1 ? '' : 's'}</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="lg:hidden space-y-3">
          {group.items.map((entry) => (
            <ProcurementMobileCard
              key={entry.item.id}
              entry={entry}
              viewMode={viewMode}
              saving={saving}
              allBreakdownsExpanded={allBreakdownsExpanded}
              onDraftChange={onDraftChange}
              onInventoryChange={onInventoryChange}
              onToggleExcluded={onToggleExcluded}
              onVendorChange={onVendorChange}
              ingredientVendors={ingredientVendorsCache.get(entry.item.ingredientId)}
              onFetchIngredientVendors={onFetchIngredientVendors}
              regenDiffEntry={regenDiff[entry.item.id]}
              saveState={rowSaveStatus[entry.item.id] ?? IDLE_SAVE_STATE}
              onRowBlur={onRowBlur}
            />
          ))}
        </div>
        <div className="hidden overflow-x-auto lg:block">
          <table className="w-full min-w-[850px] text-sm">
            <thead>
              <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="pb-3 pr-4 font-medium">Name</th>
                <th className="pb-3 pr-4 font-medium">Recipe Qty</th>
                <th className="pb-3 pr-4 font-medium">On Hand</th>
                <th className="pb-3 pr-4 font-medium">Purchase Qty</th>
                <th className="pb-3 pr-4 font-medium">Unit</th>
                <th className="pb-3 pr-4 font-medium">Notes</th>
                <th className="w-12 pb-3 text-right font-medium" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {group.items.map((entry) => (
                <ProcurementTableRow
                  key={entry.item.id}
                  entry={entry}
                  viewMode={viewMode}
                  saving={saving}
                  allBreakdownsExpanded={allBreakdownsExpanded}
                  onDraftChange={onDraftChange}
                  onInventoryChange={onInventoryChange}
                  onToggleExcluded={onToggleExcluded}
                  onVendorChange={onVendorChange}
                  ingredientVendors={ingredientVendorsCache.get(entry.item.ingredientId)}
                  onFetchIngredientVendors={onFetchIngredientVendors}
                  regenDiffEntry={regenDiff[entry.item.id]}
                  saveState={rowSaveStatus[entry.item.id] ?? IDLE_SAVE_STATE}
                  onRowBlur={onRowBlur}
                />
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function ProcurementMobileCard({
  entry,
  viewMode,
  saving,
  allBreakdownsExpanded,
  onDraftChange,
  onInventoryChange,
  onToggleExcluded,
  onVendorChange,
  ingredientVendors,
  onFetchIngredientVendors,
  regenDiffEntry,
  saveState,
  onRowBlur,
}: {
  entry: ProcurementDisplayItem;
  viewMode: ProcurementViewMode;
  saving: boolean;
  allBreakdownsExpanded: boolean | null;
  onDraftChange: (itemId: number, patch: Partial<ProcurementItemDraft>) => void;
  onInventoryChange: (itemId: number, inventoryValue: string) => void;
  onToggleExcluded: (itemId: number) => void;
  onVendorChange: (itemId: number, vendorId: number | null, vendorName: string | null) => void;
  ingredientVendors: IngredientVendor[] | undefined;
  onFetchIngredientVendors: (ingredientId: number) => Promise<void>;
  regenDiffEntry?: RegenDiffEntry;
  saveState: QuantitySaveState;
  onRowBlur?: (itemId: number) => void;
}) {
  const {
    item,
    draft,
    isNewItem,
    isManual,
    isStaple,
    sourceLabel,
    hasUnsavedChange,
    rowHighlightClass,
    quantityInputClass,
    inventoryInputClass,
    notesInputClass,
    actionClass,
    contextLabel,
    vendorSelectClass,
  } = getProcurementRowState(entry, viewMode);
  const hasNotes = draft.notes.trim().length > 0;
  const [isNotesExpanded, setIsNotesExpanded] = useState(() => hasNotes);
  const [localBreakdownExpanded, setLocalBreakdownExpanded] = useState(false);
  const isBreakdownExpanded = allBreakdownsExpanded ?? localBreakdownExpanded;
  const breakdown = item.calculationBreakdown ?? [];
  const hasBreakdown = !isManual && breakdown.length > 0;
  const packCount = isManual ? null : formatPackCount(draft.purchaseQuantity, getItemOrderIncrement(item), item.unit);
  const notesToggleLabel = isNotesExpanded
    ? 'Hide notes'
    : hasNotes
      ? 'Show notes'
      : 'Add notes';

  return (
    <div
      className={`space-y-4 rounded-lg border p-4 ${rowHighlightClass} ${regenDiffEntry ? 'border-l-[3px] border-l-amber-500' : ''}`}
      onBlur={(event) => handleRowFocusOut(event, onRowBlur, item.id)}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`font-medium ${draft.isExcluded ? 'line-through' : ''}`}>
              {item.displayName}
            </span>
            <Badge variant={isManual || isStaple ? 'secondary' : 'outline'} title={isStaple ? 'Weekly staple: added every week regardless of RSVPs' : undefined}>
              {sourceLabel}
            </Badge>
            {hasUnsavedChange && (
              <Badge variant={isNewItem ? 'secondary' : 'outline'}>
                Unsaved
              </Badge>
            )}
          </div>
          <div className="space-y-0.5">
            {viewMode === 'vendor' && (
              <div className={`text-xs text-muted-foreground ${draft.isExcluded ? 'line-through' : ''}`}>
                {contextLabel}
              </div>
            )}
            <VendorSelect
              item={item}
              draft={draft}
              vendorSelectClass={vendorSelectClass}
              saving={saving}
              ingredientVendors={ingredientVendors}
              onVendorChange={onVendorChange}
              onFetchIngredientVendors={onFetchIngredientVendors}
            />
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onToggleExcluded(item.id)}
          disabled={saving}
          className={actionClass}
          aria-label={draft.isExcluded ? `Restore ${item.displayName}` : `Exclude ${item.displayName}`}
          title={draft.isExcluded ? 'Restore item' : 'Strike out item'}
        >
          {draft.isExcluded ? (
            <RotateCcw className="h-4 w-4" />
          ) : (
            <X className="h-4 w-4" />
          )}
        </Button>
      </div>

      <div className={`text-sm text-muted-foreground ${draft.isExcluded ? 'line-through' : ''}`}>
        <span className="inline-flex items-center gap-1.5">
          {isStaple ? 'Weekly Target' : 'Recipe Qty'}: {isManual ? '--' : isStaple ? `${formatQuantity(item.calculatedQuantity)} ${item.unit}` : `${formatRecipeQuantity(item)} ${item.unit}`}
          {hasBreakdown && (
            <button
              type="button"
              onClick={() => setLocalBreakdownExpanded((v) => !v)}
              className="inline-flex items-center text-muted-foreground hover:text-foreground"
              aria-expanded={isBreakdownExpanded}
              aria-label={`${isBreakdownExpanded ? 'Hide' : 'Show'} calculation breakdown for ${item.displayName}`}
            >
              <Info className="h-3.5 w-3.5" />
            </button>
          )}
          {hasBreakdown && hasUnitMismatch(breakdown) && (
            <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
          )}
        </span>
        {regenDiffEntry && regenDiffEntry.prevCalc !== item.calculatedQuantity && (
          <span className="text-xs text-amber-600"> was {formatQuantity(regenDiffEntry.prevCalc)}</span>
        )}
      </div>
      {hasBreakdown && isBreakdownExpanded && (
        <BreakdownPanel breakdown={breakdown} parentUnit={item.unit} />
      )}

      <div className="space-y-4">
        {!isManual && (
          <div className="space-y-2">
            <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              On Hand{entry.inventoryUnit && entry.inventoryUnit !== item.unit ? ` (${entry.inventoryUnit})` : ''}
            </label>
            {entry.inventoryTrackedIn ? (
              <InventoryTrackedElsewhere item={item} trackedIn={entry.inventoryTrackedIn} />
            ) : (
              <>
                <Input
                  value={draft.inventoryQuantity}
                  onChange={(event) => onInventoryChange(item.id, event.target.value)}
                  aria-label={`${item.displayName} on hand quantity`}
                  className={inventoryInputClass}
                  disabled={saving}
                  placeholder="0"
                />
                <StaleInventoryNote entry={entry} />
              </>
            )}
          </div>
        )}

        <div className="space-y-2">
          <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Purchase Qty
          </label>
          <Input
            value={draft.purchaseQuantity}
            onChange={(event) => onDraftChange(item.id, { purchaseQuantity: event.target.value })}
            aria-label={`${item.displayName} purchase quantity`}
            className={quantityInputClass}
            disabled={saving}
          />
          <RowSaveStatus state={saveState} className="w-full" />
          {packCount ? <div className="text-xs text-muted-foreground">{packCount}</div> : null}
          {regenDiffEntry && regenDiffEntry.prevPurchase !== item.purchaseQuantity && (
            <div className="text-xs text-amber-600">was {formatQuantity(regenDiffEntry.prevPurchase)}</div>
          )}
        </div>
        <div className="space-y-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => setIsNotesExpanded((expanded) => !expanded)}
            className="h-auto w-full justify-between px-0 py-0 text-left hover:bg-transparent"
            aria-expanded={isNotesExpanded}
            aria-controls={`procurement-notes-${item.id}`}
            disabled={saving}
          >
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Notes
            </span>
            <span className="flex items-center gap-2 text-xs text-muted-foreground">
              {notesToggleLabel}
              {isNotesExpanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </span>
          </Button>
          {isNotesExpanded && (
            <Input
              id={`procurement-notes-${item.id}`}
              value={draft.notes}
              onChange={(event) => onDraftChange(item.id, { notes: event.target.value })}
              placeholder="Notes"
              aria-label={`${item.displayName} notes`}
              className={notesInputClass}
              disabled={saving}
            />
          )}
        </div>
      </div>

      <div className={`text-sm text-muted-foreground ${draft.isExcluded ? 'line-through' : ''}`}>
        Unit: {item.unit}
      </div>
    </div>
  );
}

function ProcurementTableRow({
  entry,
  viewMode,
  saving,
  allBreakdownsExpanded,
  onDraftChange,
  onInventoryChange,
  onToggleExcluded,
  onVendorChange,
  ingredientVendors,
  onFetchIngredientVendors,
  regenDiffEntry,
  saveState,
  onRowBlur,
}: {
  entry: ProcurementDisplayItem;
  viewMode: ProcurementViewMode;
  saving: boolean;
  allBreakdownsExpanded: boolean | null;
  onDraftChange: (itemId: number, patch: Partial<ProcurementItemDraft>) => void;
  onInventoryChange: (itemId: number, inventoryValue: string) => void;
  onToggleExcluded: (itemId: number) => void;
  onVendorChange: (itemId: number, vendorId: number | null, vendorName: string | null) => void;
  ingredientVendors: IngredientVendor[] | undefined;
  onFetchIngredientVendors: (ingredientId: number) => Promise<void>;
  regenDiffEntry?: RegenDiffEntry;
  saveState: QuantitySaveState;
  onRowBlur?: (itemId: number) => void;
}) {
  const [localBreakdownExpanded, setLocalBreakdownExpanded] = useState(false);
  const {
    item,
    draft,
    isManual,
    isStaple,
    sourceLabel,
    contextLabel,
    rowHighlightClass,
    quantityInputClass,
    inventoryInputClass,
    notesInputClass,
    actionClass,
    vendorSelectClass,
  } = getProcurementRowState(entry, viewMode);

  const isBreakdownExpanded = allBreakdownsExpanded ?? localBreakdownExpanded;
  const breakdown = item.calculationBreakdown ?? [];
  const hasBreakdown = !isManual && breakdown.length > 0;
  const packCount = isManual ? null : formatPackCount(draft.purchaseQuantity, getItemOrderIncrement(item), item.unit);

  return (
    <>
      <tr
        className={`border-b align-top last:border-b-0 ${isBreakdownExpanded && hasBreakdown ? 'border-b-0' : ''} ${rowHighlightClass} ${regenDiffEntry ? 'border-l-[3px] border-l-amber-500' : ''}`}
        onBlur={(event) => handleRowFocusOut(event, onRowBlur, item.id)}
      >
        <td className={`py-4 pr-4 ${regenDiffEntry ? 'pl-3' : ''}`}>
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`font-medium ${draft.isExcluded ? 'line-through' : ''}`}>
                {item.displayName}
              </span>
              <Badge variant={isManual || isStaple ? 'secondary' : 'outline'} title={isStaple ? 'Weekly staple: added every week regardless of RSVPs' : undefined}>
                {sourceLabel}
              </Badge>
            </div>
            <div className="space-y-0.5">
              {viewMode === 'vendor' && (
                <div className={`text-xs text-muted-foreground ${draft.isExcluded ? 'line-through' : ''}`}>
                  {contextLabel}
                </div>
              )}
              <VendorSelect
                item={item}
                draft={draft}
                vendorSelectClass={vendorSelectClass}
                saving={saving}
                ingredientVendors={ingredientVendors}
                onVendorChange={onVendorChange}
                onFetchIngredientVendors={onFetchIngredientVendors}
              />
            </div>
          </div>
        </td>
        <td className={`align-middle py-4 pr-4 text-sm text-muted-foreground ${draft.isExcluded ? 'line-through' : ''}`}>
          <span className="inline-flex items-center gap-1.5">
            {isManual ? '--' : isStaple ? `${formatQuantity(item.calculatedQuantity)} target` : formatRecipeQuantity(item)}
            {hasBreakdown && (
              <button
                type="button"
                onClick={() => setLocalBreakdownExpanded((v) => !v)}
                className="inline-flex items-center text-muted-foreground hover:text-foreground"
                aria-expanded={isBreakdownExpanded}
                aria-label={`${isBreakdownExpanded ? 'Hide' : 'Show'} calculation breakdown for ${item.displayName}`}
              >
                <Info className="h-3.5 w-3.5" />
              </button>
            )}
            {hasBreakdown && hasUnitMismatch(breakdown) && (
              <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
            )}
          </span>
          {regenDiffEntry && regenDiffEntry.prevCalc !== item.calculatedQuantity && (
            <div className="text-xs text-amber-600">was {formatQuantity(regenDiffEntry.prevCalc)}</div>
          )}
        </td>
        <td className="align-middle py-4 pr-4">
          {isManual ? (
            <span className="text-sm text-muted-foreground">--</span>
          ) : entry.inventoryTrackedIn ? (
            <InventoryTrackedElsewhere item={item} trackedIn={entry.inventoryTrackedIn} />
          ) : (
            <div className="flex items-center gap-1.5">
              <Input
                value={draft.inventoryQuantity}
                onChange={(event) => onInventoryChange(item.id, event.target.value)}
                aria-label={`${item.displayName} on hand quantity`}
                className={`w-20 ${inventoryInputClass}`}
                disabled={saving}
                placeholder="0"
              />
              {entry.inventoryUnit && entry.inventoryUnit !== item.unit && (
                <span className="text-xs text-muted-foreground" title={`On hand is tracked in ${entry.inventoryUnit}`}>{entry.inventoryUnit}</span>
              )}
              <StaleInventoryNote entry={entry} />
            </div>
          )}
        </td>
        <td className="align-middle py-4 pr-4">
          <Input
            value={draft.purchaseQuantity}
            onChange={(event) => onDraftChange(item.id, { purchaseQuantity: event.target.value })}
            aria-label={`${item.displayName} purchase quantity`}
            className={`w-20 ${quantityInputClass}`}
            disabled={saving}
          />
          <RowSaveStatus state={saveState} className="mt-1 w-20" />
          {packCount ? <div className="text-xs text-muted-foreground whitespace-nowrap">{packCount}</div> : null}
          {regenDiffEntry && regenDiffEntry.prevPurchase !== item.purchaseQuantity && (
            <div className="text-xs text-amber-600">was {formatQuantity(regenDiffEntry.prevPurchase)}</div>
          )}
        </td>
        <td className={`align-middle py-4 pr-4 text-sm text-muted-foreground ${draft.isExcluded ? 'line-through' : ''}`}>
          {item.unit}
        </td>
        <td className="align-middle py-4 pr-4">
          <Input
            value={draft.notes}
            onChange={(event) => onDraftChange(item.id, { notes: event.target.value })}
            placeholder="Notes"
            aria-label={`${item.displayName} notes`}
            className={notesInputClass}
            disabled={saving}
          />
        </td>
        <td className="align-middle py-4 text-right">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onToggleExcluded(item.id)}
            disabled={saving}
            className={actionClass}
            aria-label={draft.isExcluded ? `Restore ${item.displayName}` : `Exclude ${item.displayName}`}
            title={draft.isExcluded ? 'Restore item' : 'Strike out item'}
          >
            {draft.isExcluded ? (
              <RotateCcw className="h-4 w-4" />
            ) : (
              <X className="h-4 w-4" />
            )}
          </Button>
        </td>
      </tr>
      {hasBreakdown && isBreakdownExpanded && (
        <tr className={`border-b last:border-b-0 ${rowHighlightClass}`}>
          <td colSpan={7} className="pb-4 pl-4 pr-4">
            <BreakdownPanel breakdown={breakdown} parentUnit={item.unit} />
          </td>
        </tr>
      )}
    </>
  );
}
